import Highcharts from 'highcharts';
import moment from 'moment';
import { makeAutoObservable } from 'mobx';
import {debounce, throttle, uniq} from 'lodash';

import { apiFetch, PagedList } from 'framework/api';
import { GridDataItem } from 'controls/grid/gridDataItem';
import { WidgetProps } from 'controls/designer/features/widgets/widgetProps';
import { MobxManager } from 'framework/mobx-integration';
import { RemoteEventsManager } from 'core/remoteEventsManager';
import { DataSourceEvent } from 'framework/entities/events';
import { TimePeriod } from 'controls/react/form/timePeriodSelector';
import { newGuid } from 'tools/guid';
import { formatNumber } from 'tools/helpers/math';
import { ApplicationState } from 'framework/applicationState';
import {
	clearDatasourceCache,
	DataSourceDataItem,
	getDataSourceData,
} from 'controls/designer/features/widgets/datasources/api';
import { updatedPayloadToLoadAllData } from 'controls/designer/features/widgets/datasources/shared/utils/updatedPayloadToLoadAllData';

import { DataSourceSingleGraphDataSourceConfig, DataSourceSingleGraphWidgetConfig } from './types';
import { buildSeriesColor } from './dataSourceSingleGraphSeriesColor';

type SeriesState = {
	row: DataSourceSingleGraphDataSourceConfig;
	requestId: string;
	data?: PagedList<GridDataItem<DataSourceDataItem>>;
};

export class DataSourceSingleGraphStore {
	loading = false;
	errorMessage: string;
	series: SeriesState[] = [];

	initialized = false;
	unsubscriber: { subscriberId: string; unsubscribe: () => void; releaseEvents: (data: unknown) => void };
	mobx = new MobxManager();

	get config() {
		return this.props.config;
	}

	get timePeriod() {
		return this.props.config.period;
	}
	set timePeriod(value: TimePeriod) {
		this.props.config.period = value;
	}

	constructor(private props: WidgetProps<DataSourceSingleGraphWidgetConfig>) {
		makeAutoObservable(this);

		this.series = (this.config.dataSources ?? []).map((row) => ({ row, requestId: newGuid() }));

		this.mobx.reaction(() => this.timePeriod, this.loadData);
		this.mobx.reaction(
			() => this.props.designer.store.timeSettings?.period,
			(period) => {
				if (period) this.onPeriodChange(period);
			},
			{ fireImmediately: true },
		);
	}

	init = async () => {
		this.loading = true;
		await this.loadData();
		this.subscribe();
		this.initialized = true;
		this.loading = false;
	};

	loadData = async () => {
		const responses = await Promise.all(
			this.series.map(async (entry) => {
				if (!entry.row.dataSourceId) return { entry, response: null };

				const request = getDataSourceData({
					accountId: this.config.accountId,
					dataSourceId: entry.row.dataSourceId,
					requestId: entry.requestId,
				});
				updatedPayloadToLoadAllData(request);
				request.payload.timePeriod = this.timePeriod;

				return { entry, response: await apiFetch(request) };
			}),
		);

		const errors: string[] = [];
		responses.forEach(({ entry, response }) => {
			if (!response) {
				entry.data = undefined;
				return;
			}
			if (response.success) {
				entry.data = response.data;
			} else {
				entry.data = undefined;
				errors.push(response.message);
			}
		});
		this.errorMessage = uniq(errors)
			.filter((x) => !!x)
			.join(';\r\n');
	};

	subscribe() {
		this.unsubscriber?.unsubscribe();
		const subscriptionObj = this.series
			.filter((x) => x.row.dataSourceId)
			.map((x) => DataSourceEvent.subscription(x.row.dataSourceId, this.config.accountId));
		if (!subscriptionObj.length) return;

		this.unsubscriber = RemoteEventsManager.subscribeCallback(subscriptionObj, this.onEvent);
	}

	onEvent = throttle(() => {
		void this.loadData();
	}, 30000);

	onPeriodChange = (period: TimePeriod) => {
		if (JSON.stringify(this.props.config.period) == JSON.stringify(period)) {
			return;
		}
		this.timePeriod = period;
	};

	onPeriodChangeDebounced = debounce(this.onPeriodChange, 1000);

	getSeriesLabel(row: DataSourceSingleGraphDataSourceConfig) {
		const datasourceLabel = row.datasourceName || row.dataSourceId || '';
		const metricLabel = row.metricRowName || row.metricColumn;
		const label = metricLabel ? `${datasourceLabel}-${metricLabel}` : datasourceLabel;
		return row.rowKey && row.rowKeyValue ? `${label} (${row.rowKeyValue})` : label;
	}

	getSeriesPoints(entry: SeriesState): number[][] {
		if (!entry.data?.items?.length || !entry.row.timeColumn || !entry.row.metricColumn) {
			return [];
		}
		const filteredItems = entry.row.rowKey
			? entry.data.items.filter((x) => String(x.data[entry.row.rowKey]) === String(entry.row.rowKeyValue))
			: entry.data.items;
		const rows = [...filteredItems].sort(
			(a, b) => moment(a.data[entry.row.timeColumn]).valueOf() - moment(b.data[entry.row.timeColumn]).valueOf(),
		);
		return rows.map((row) => {
			const x = moment(row.data[entry.row.timeColumn]).valueOf();
			const y = Number(row.data[entry.row.metricColumn]) || 0;
			return entry.row.chartType === 'arearange' ? [x, 0, y] : [x, y];
		});
	}

	get chartOptions(): Highcharts.Options {
		const decimalsNumber = this.config.decimalsNumber;
		const entries = this.series.filter((x) => x.row.dataSourceId);

		const series: Highcharts.SeriesOptionsType[] = entries.map((entry) => {
			const isLine = entry.row.chartType === 'line';
			const colors = buildSeriesColor(entry.row.colorSettings);

			return {
				type: entry.row.chartType,
				name: this.getSeriesLabel(entry.row),
				data: this.getSeriesPoints(entry),
				color: colors.color,
				...(isLine ? {} : { fillColor: colors.fillColor }),
			};
		});

		return {
			chart: {
				type: entries[0]?.row.chartType ?? 'line',
			},
			legend: { enabled: true },
			xAxis: {
				type: 'datetime',
			},
			yAxis: {
				title: { text: '' },
			},
			tooltip: {
				backgroundColor: 'white',
				borderWidth: 0,
				useHTML: true,
				shared: entries.length > 1,
				formatter: function () {
					const ctx = this as unknown as {
						x: number;
						y: number;
						series?: { name: string };
						point?: { high: number };
						points?: Array<{ series: { name: string }; y: number; point?: { high: number } }>;
					};
					const points = ctx.points ?? [{ series: ctx.series, y: ctx.y, point: ctx.point }];
					const rows = points.map(
						(point) => `${point.series.name}: ${formatNumber(point.point?.high ?? point.y, decimalsNumber)}`,
					);
					return `${moment(ctx.x).format(ApplicationState.momentDateTimeFormat)}<br/>${rows.join('<br/>')}`;
				},
			},
			series,
		};
	}

	redirect = () => {
		this.props.navigator.go({});
	};

	destroy() {
		this.unsubscriber?.unsubscribe();
		this.mobx.destroy();
		this.series.forEach((entry) => {
			void apiFetch(clearDatasourceCache({ accountId: this.config.accountId, requestId: entry.requestId }));
		});
	}
}
